import React from "react";
import {
  Integration,
  PreexistingProductionLinkedAccountPreference,
} from "../../../../../models/Entities";

export type MergeLinkCustomizationStatus = "PUBLISHED" | "STAGING";

export type MergeLinkCustomizationModalType = "PUBLISH" | "DISCARD" | "VIEW_CHANGES";

export type WhiteLabelGuideType = {
  subdomain: string;
  square_logo: File | null;
  active: boolean;
};

export type OrganizationCustomization = {
  status: MergeLinkCustomizationStatus;
  auto_enable_new_integrations?: boolean | null;
  show_merge_footer?: boolean | null;
  show_selective_sync_screen?: boolean | null;
  show_scopes_screen?: boolean | null;
  primary_button_color?: string | null;
  primary_button_text_color?: string | null;
  button_border_radius?: string | null;
  modal_border_radius?: string | null;
  selection_button_border_radius?: string | null;
  font?: string | null;
  preexisting_production_linked_account_preference?: PreexistingProductionLinkedAccountPreference | null;
  show_cobranding?: boolean | null;
  white_label_guide_configuration?: WhiteLabelGuideType | null;
  show_white_label_guide?: boolean | null;
};

export type IntegrationCustomization = {
  status: MergeLinkCustomizationStatus;
  integration: number;
  category: string;
  help_center_authentication_message?: string | null;
  help_center_authentication_url?: string | null;
  help_center_validation_url?: string | null;
};

export interface CustomizationContextState {
  isLoading: boolean;
  organizationCustomization: OrganizationCustomization | null;
  setOrganizationCustomization: (organizationCustomization: OrganizationCustomization) => void;
  integrationCustomizations: IntegrationCustomization[] | null;
  setIntegrationCustomizations: (integrationCustomizations: IntegrationCustomization[]) => void;
  modalTypeShown: MergeLinkCustomizationModalType | null;
  setModalTypeShown: (modalTypeShown: MergeLinkCustomizationModalType | null) => void;
  refreshCustomizationData: () => void;
  originalOrganizationCustomization: OrganizationCustomization | null;
  setOriginalOrganizationCustomization: (
    originalOrganizationCustomization: OrganizationCustomization | null,
  ) => void;
  setOriginalIntegrationCustomizations: (
    originalIntegrationCustomization: IntegrationCustomization[] | null,
  ) => void;
  originalIntegrationCustomizations: IntegrationCustomization[] | null;
  nextLocation: Location | null;
  setIsNavigating: (isNavigating: boolean) => void;
  logo: File | string | null;
  setLogo: (logo: File | string | null) => void;
  hasLogoChange: boolean;
  setHasLogoChange: (hasLogoChange: boolean) => void;
}

const CustomizationContext = React.createContext<CustomizationContextState>({
  refreshCustomizationData: () => {},
  isLoading: true,
  organizationCustomization: {
    status: "PUBLISHED",
    auto_enable_new_integrations: true,
    show_merge_footer: false,
    show_selective_sync_screen: null,
    show_scopes_screen: null,
    primary_button_color: "FFFFFF",
    button_border_radius: null,
    modal_border_radius: null,
    selection_button_border_radius: null,
    font: null,
    preexisting_production_linked_account_preference:
      PreexistingProductionLinkedAccountPreference.CREATE_AS_NEW_END_USER,
    show_cobranding: false,
  },
  setOrganizationCustomization: () => {},
  integrationCustomizations: [],
  setIntegrationCustomizations: () => {},
  modalTypeShown: null,
  setModalTypeShown: () => {},
  originalIntegrationCustomizations: [],
  originalOrganizationCustomization: null,
  setOriginalIntegrationCustomizations: () => {},
  setOriginalOrganizationCustomization: () => {},
  nextLocation: null,
  setIsNavigating: () => {},
  logo: null,
  setLogo: () => {},
  hasLogoChange: false,
  setHasLogoChange: () => {},
});

export default CustomizationContext;
